import { useMemo, useState } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export function DataTable({ columns, rows, searchKeys = [], testid, pageSize = 10, toolbar,
  empty = "Aucune donnée", serverSide = false, serverTotal = 0, serverPage = 1,
  serverPages = 1, onPageChange, onSearch }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    if (serverSide || !q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(needle)));
  }, [rows, q, searchKeys, serverSide]);

  const pages = serverSide ? serverPages : Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = serverSide ? serverPage - 1 : Math.min(page, pages - 1);
  const slice = serverSide ? filtered : filtered.slice(cur * pageSize, cur * pageSize + pageSize);
  const totalCount = serverSide ? serverTotal : filtered.length;
  const showSearch = searchKeys.length > 0 || !!onSearch;

  const handleSearch = (v) => {
    setQ(v);
    setPage(0);
    if (serverSide && onSearch) onSearch(v);
  };

  const goPage = (p) => {
    if (serverSide && onPageChange) onPageChange(p + 1);
    else setPage(p);
  };

  return (
    <div data-testid={testid} className="bg-card border rounded-xl overflow-hidden">
      {(showSearch || toolbar) && (
        <div className="flex flex-wrap items-center gap-3 p-3 border-b">
          {showSearch && (
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid={`${testid}-search`}
                value={q}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Rechercher..."
                className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
          {toolbar}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              {columns.map((c) => (
                <th key={c.key} className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-muted-foreground ${c.className || ""}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-muted-foreground">{empty}</td></tr>
            )}
            {slice.map((r, i) => (
              <tr key={r.id || i} data-testid={`${testid}-row-${r.id || i}`} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
        <span data-testid={`${testid}-count`}>{totalCount} élément(s)</span>
        <div className="flex items-center gap-2">
          <button data-testid={`${testid}-prev`} disabled={cur === 0} onClick={() => goPage(cur - 1)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border disabled:opacity-40 hover:bg-muted">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span data-testid={`${testid}-page`}>{cur + 1} / {pages}</span>
          <button data-testid={`${testid}-next`} disabled={cur >= pages - 1} onClick={() => goPage(cur + 1)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border disabled:opacity-40 hover:bg-muted">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
